import AddCircleOutline from '@mui/icons-material/AddCircleOutline';
import DeleteOutline from '@mui/icons-material/DeleteOutline';
import FlashOn from '@mui/icons-material/FlashOn';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControl from '@mui/material/FormControl';
import IconButton from '@mui/material/IconButton';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Select from '@mui/material/Select';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import toastError from "../../errors/toastError";
import api from "../../services/api";

const MATCH_TYPES = [
  { value: "contains", label: "Contém" },
  { value: "exact", label: "Exato" },
  { value: "starts_with", label: "Começa com" },
];

const emptyTrigger = () => ({
  scriptId: "",
  keyword: "",
  matchType: "contains",
  active: true,
  maxFires: "",
  cooldownHours: ""
});

const ScriptTriggerModal = ({ open, onClose, scripts }) => {
  const [triggers, setTriggers] = useState([]);
  const [newTrigger, setNewTrigger] = useState(emptyTrigger());
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    api.get("/scriptTriggers").then(({ data }) => {
      setTriggers(data);
      setLoading(false);
    }).catch(err => { toastError(err); setLoading(false); });
  }, [open]);

  const handleAdd = async () => {
    if (!newTrigger.scriptId) { toast.error("Selecione um Speed Mode"); return; }
    if (!newTrigger.keyword.trim()) { toast.error("Digite uma palavra-chave"); return; }

    setSaving(true);
    try {
      await api.post("/scriptTriggers", {
        ...newTrigger,
        maxFires: newTrigger.maxFires !== "" ? Number(newTrigger.maxFires) : null,
        cooldownHours: newTrigger.cooldownHours !== "" ? Number(newTrigger.cooldownHours) : null
      });
      const { data: full } = await api.get("/scriptTriggers");
      setTriggers(full);
      setNewTrigger(p => ({ ...emptyTrigger(), scriptId: p.scriptId }));
      toast.success("Gatilho criado!");
    } catch (err) {
      toastError(err);
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async (trigger) => {
    try {
      const { data } = await api.put(`/scriptTriggers/${trigger.id}`, { active: !trigger.active });
      setTriggers(prev => prev.map(t => t.id === trigger.id ? { ...t, active: data.active } : t));
    } catch (err) { toastError(err); }
  };

  const handleDelete = async (triggerId) => {
    try {
      await api.delete(`/scriptTriggers/${triggerId}`);
      setTriggers(prev => prev.filter(t => t.id !== triggerId));
      toast.success("Gatilho excluído");
    } catch (err) { toastError(err); }
  };

  const matchLabel = (type) => MATCH_TYPES.find(m => m.value === type)?.label || type;

  const fireLabel = (trigger) => {
    if (!trigger.maxFires) return null;
    const limit = `${trigger.maxFires}x`;
    const cooldown = trigger.cooldownHours ? ` / reset ${trigger.cooldownHours}h` : "";
    return limit + cooldown;
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <FlashOn color="warning" />
        Gatilhos Automáticos
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
          Quando um cliente enviar uma mensagem com a palavra-chave, o script selecionado será disparado automaticamente.
        </Typography>

        {/* Form novo gatilho */}
        <Paper variant="outlined" sx={{ p: 1.5, mb: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>Novo Gatilho</Typography>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            <Box sx={{ display: "flex", gap: 1 }}>
              <TextField
                label="Palavra-chave(s)"
                value={newTrigger.keyword}
                onChange={e => setNewTrigger(p => ({ ...p, keyword: e.target.value }))}
                size="small"
                fullWidth
                placeholder="Ex: plano; planos; preco"
                helperText="Separe multiplas palavras com ; — ignora acentos automaticamente"
              />
              <FormControl size="small" sx={{ minWidth: 130 }}>
                <InputLabel>Tipo</InputLabel>
                <Select
                  value={newTrigger.matchType}
                  label="Tipo"
                  onChange={e => setNewTrigger(p => ({ ...p, matchType: e.target.value }))}
                >
                  {MATCH_TYPES.map(m => (
                    <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
              <TextField
                label="Limite de disparos"
                value={newTrigger.maxFires}
                onChange={e => setNewTrigger(p => ({ ...p, maxFires: e.target.value }))}
                size="small"
                type="number"
                inputProps={{ min: 1 }}
                sx={{ width: 150 }}
                placeholder="Ex: 1"
                helperText="Por lead (vazio = sem limite)"
              />
              <TextField
                label="Reset após (horas)"
                value={newTrigger.cooldownHours}
                onChange={e => setNewTrigger(p => ({ ...p, cooldownHours: e.target.value }))}
                size="small"
                type="number"
                inputProps={{ min: 1 }}
                sx={{ width: 170 }}
                placeholder="Ex: 24"
                helperText="Zera contador após X horas"
              />
            </Box>
            <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
              <FormControl size="small" fullWidth>
                <InputLabel>Script</InputLabel>
                <Select
                  value={newTrigger.scriptId}
                  label="Script"
                  onChange={e => setNewTrigger(p => ({ ...p, scriptId: e.target.value }))}
                >
                  {scripts.map(s => (
                    <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Button
                variant="contained"
                startIcon={<AddCircleOutline />}
                onClick={handleAdd}
                disabled={saving}
                sx={{ whiteSpace: "nowrap" }}
              >
                Adicionar
              </Button>
            </Box>
          </Box>
        </Paper>

        {/* Lista de gatilhos */}
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Gatilhos ativos ({triggers.length})
        </Typography>

        {loading ? (
          <Typography color="textSecondary" variant="body2">Carregando...</Typography>
        ) : triggers.length === 0 ? (
          <Typography color="textSecondary" variant="body2" sx={{ textAlign: "center", py: 2 }}>
            Nenhum gatilho configurado
          </Typography>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            {triggers.map(trigger => (
              <Paper
                key={trigger.id}
                variant="outlined"
                sx={{
                  p: 1,
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  opacity: trigger.active ? 1 : 0.5,
                  borderLeft: `4px solid ${trigger.active ? "#ed6c02" : "#ccc"}`
                }}
              >
                <Switch
                  checked={trigger.active}
                  onChange={() => handleToggle(trigger)}
                  size="small"
                  color="warning"
                />
                <Box sx={{ flex: 1 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.25 }}>
                    <Typography variant="body2" fontWeight={600}>
                      "{trigger.keyword}"
                    </Typography>
                    <Chip label={matchLabel(trigger.matchType)} size="small" variant="outlined" sx={{ fontSize: "0.65rem" }} />
                    {fireLabel(trigger) && (
                      <Chip label={fireLabel(trigger)} size="small" color="warning" variant="outlined" sx={{ fontSize: "0.65rem" }} />
                    )}
                  </Box>
                  <Typography variant="caption" color="textSecondary">
                    {"→"} {trigger.script?.name || "Script removido"}
                  </Typography>
                </Box>
                <Tooltip title="Excluir gatilho">
                  <IconButton size="small" onClick={() => handleDelete(trigger.id)}>
                    <DeleteOutline color="error" fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Paper>
            ))}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Fechar</Button>
      </DialogActions>
    </Dialog>
  );
};

export default ScriptTriggerModal;
